// AGC: LotHeader - Compact header with lot identity and production information
// AGC: Shows: field name, lot name, crop, area, planting date, growth stage, production estimate
// AGC: Updated to use CROP IMAGE BACKGROUND (like lot cards) instead of green gradient

import React, { useState } from 'react'
import { 
  Calendar, 
  Leaf, 
  Target,
  Sprout,
  Bean,
  Wheat,
  Flower2,
  Coffee
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { QuickAddMenu } from '../notes/QuickAddMenu'
import { getCropImageUrl } from '../../config/localCropImages'
// AGC: [#9] Translate the phenological stage name by locale (Brazil/pt sees the same text).
import { growthStageLabel } from '../../lib/growthStageLabel'
import type { LotHeaderData } from '../../types'

interface LotHeaderProps {
  data: LotHeaderData
  onBack: () => void
  noteCount?: number
  onPhotoCapture?: () => void
  onVideoCapture?: () => void
  onAudioNote?: () => void
  onTextNote?: () => void
  onTimelineClick?: () => void  // AGC: [PLOT_TIMELINE] Handler for opening timeline overlay
}

// AGC: Map crop types to Lucide icons
const getCropIcon = (cropName: string): React.ComponentType<{ className?: string }> => {
  const cropLower = cropName.toLowerCase()
  if (cropLower.includes('soja') || cropLower.includes('soy') || cropLower.includes('feijao') || cropLower.includes('bean')) return Bean
  if (cropLower.includes('milho') || cropLower.includes('corn') || cropLower.includes('trigo') || cropLower.includes('wheat')) return Wheat
  if (cropLower.includes('algodao') || cropLower.includes('cotton')) return Flower2
  if (cropLower.includes('cafe') || cropLower.includes('coffee')) return Coffee
  return Sprout // AGC: Default icon
}

/**
 * AGC: LotHeader - Displays compact lot identity and production information
 * AGC: Part of the Lot Page implementation
 * AGC: Uses hero gradient with radial glow effects for visual consistency
 */
export const LotHeader: React.FC<LotHeaderProps> = ({ 
  data, 
  onBack,
  noteCount = 0,
  onPhotoCapture,
  onVideoCapture,
  onAudioNote,
  onTextNote,
  onTimelineClick,
}) => {
  const { t } = useTranslation()
  const [imageError, setImageError] = useState(false)
  
  // AGC: Calculate cycle progress percentage
  const cycleProgress = data.cycleTotalDays > 0 
    ? Math.min(100, Math.round((data.cycleElapsedDays / data.cycleTotalDays) * 100))
    : 0

  // AGC: Format date for display
  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  // AGC: Get appropriate crop icon
  const CropIcon = getCropIcon(data.cropName)

  // AGC: Get crop image URL (same logic as lot cards)
  const cropImageUrl = getCropImageUrl(data.cropName)
  const showImage = cropImageUrl && !imageError

  return (
    <div 
      className="px-4 py-4 rounded-b-2xl relative overflow-hidden min-h-[240px]"
    >
      {/* AGC: Background Image or Fallback Gradient */}
      {showImage ? (
        <>
          <img
            src={cropImageUrl}
            alt={data.cropName}
            className="absolute inset-0 w-full h-full object-cover object-center"
            onError={() => setImageError(true)}
          />
          {/* AGC: Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
        </>
      ) : (
        // AGC: Fallback to green gradient if no image
        <div 
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
        />
      )}

      {/* AGC: Decorative background effects (only if no image) */}
      {!showImage && (
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
          />
        </div>
      )}

      {/* AGC: Main header row with icon and lot name */}
      <div className="flex items-start gap-3 mb-4 relative z-10">
        {/* AGC: Crop icon in translucent container */}
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
          <CropIcon className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          {/* AGC: Lot name */}
          <h1 className="text-white text-xl font-bold truncate">{data.lotName}</h1>
          
          {/* AGC: Crop and area */}
          <div className="flex items-center gap-2 text-white/80 text-sm mt-0.5">
            <span>{data.cropName}</span>
            <span className="text-white/50">•</span>
            <span>{data.totalHectares.toFixed(1)} ha</span>
            {noteCount > 0 && (
              <>
                <span className="text-white/50">•</span>
                <span className="flex items-center gap-1">
                  📝 {noteCount}
                </span>
              </>
            )}
          </div>
        </div>

        {/* AGC: QuickAddMenu - only show if handlers are provided */}
        {(onPhotoCapture || onVideoCapture || onAudioNote || onTextNote) && (
          <div className="flex-shrink-0">
            <QuickAddMenu
              onPhotoCapture={onPhotoCapture || (() => {})}
              onVideoCapture={onVideoCapture || (() => {})}
              onAudioNote={onAudioNote || (() => {})}
              onTextNote={onTextNote || (() => {})}
            />
          </div>
        )}
      </div>

      {/* AGC: Planting date */}
      <div className="flex items-center gap-2 text-white/80 text-sm mb-4 relative z-10">
        <Calendar className="w-4 h-4" />
        <span>{t('lotPage.planting')}: {formatDate(data.plantingDate)}</span>
      </div>

      {/* AGC: Growth stage card - CLICKABLE for timeline */}
      <button 
        onClick={onTimelineClick}
        className="w-full bg-white/15 backdrop-blur-sm rounded-xl p-3 mb-3 relative z-10 text-left hover:bg-white/20 active:bg-white/25 transition-colors cursor-pointer group"
        disabled={!onTimelineClick}
      >
        {/* AGC: Stage code and description */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-semibold">
              {data.currentStage.code}
            </span>
            <span className="text-white/60">•</span>
            <span className="text-white/80 text-sm">{growthStageLabel(data.currentStage.description, t)}</span>
          </div>
          {/* AGC: Timeline indicator */}
          {onTimelineClick && (
            <span className="text-white/60 text-xs group-hover:text-white/80 transition-colors">
              {t('plotTimeline.tapToView')} →
            </span>
          )}
        </div>

        {/* AGC: Progress bar */}
        <div className="relative h-2.5 bg-white/20 rounded-full overflow-hidden mb-2 group-hover:bg-white/30 transition-colors">
          <div 
            className="absolute inset-y-0 left-0 bg-white/80 rounded-full transition-all duration-500"
            style={{ width: `${cycleProgress}%` }}
          />
          {/* AGC: Progress marker */}
          {cycleProgress > 0 && (
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-md"
              style={{ left: `calc(${Math.max(cycleProgress, 2)}% - 4px)` }}
            />
          )}
        </div>

        {/* AGC: Days remaining */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-white/70">
            {data.cycleElapsedDays} {t('lotPage.days')}
          </span>
          <div className="flex items-center gap-1 text-white/80">
            <Target className="w-3.5 h-3.5" />
            <span className="text-xs">{data.daysToHarvest} {t('lotPage.daysTo')} {t('lotPage.harvest')}</span>
            <span className="text-white/60 ml-1 text-xs">→ {formatDate(data.estimatedHarvestDate)}</span>
          </div>
        </div>
      </button>
    </div>
  )
}

export default LotHeader
